import { VoteButton } from '@/components/VoteButton';
import { useTranslation } from '@/hooks/useTranslation';
import React, { useState } from 'react';
import './MixerCard.css';

interface MixerCardProps {
  answer: { id: string; answerValue: string };
  displayName?: string;
  hasVoted: boolean;
  voteCount: number;
  isUserAnswer: boolean;
  onVote: () => void;
  isVotingEnabled?: boolean;
}

const MixerCard: React.FC<MixerCardProps> = React.memo(
  ({
    answer,
    displayName,
    hasVoted,
    voteCount,
    isUserAnswer: _isUserAnswer,
    onVote,
    isVotingEnabled = true,
  }) => {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);

    const MAX_LENGTH = 150;
    const needsTruncation = answer.answerValue.length > MAX_LENGTH;
    const displayText =
      !isExpanded && needsTruncation
        ? answer.answerValue.substring(0, MAX_LENGTH) + '...'
        : answer.answerValue;

    return (
      <div className='bg-white border-b border-gray-200 last:border-b-0'>
        <div className='mixer-card-container'>
          <div className='mixer-card-avatar'>
            <div className='w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm bg-white border-2 border-gray-400 text-gray-900'>
              {displayName?.trim() ? (
                <span>{displayName}</span>
              ) : (
                <i className='pi pi-user text-gray-500' aria-hidden='true' />
              )}
            </div>
          </div>

          {/* Answer text and vote count */}
          <div className='mixer-card-content'>
            <div className='mixer-card-text text-sm text-gray-900 mb-1'>
              &ldquo;{displayText}&rdquo;
            </div>
            {needsTruncation && (
              <button
                className='text-xs text-blue-700 hover:underline block mb-1'
                onClick={() => setIsExpanded(!isExpanded)}
                aria-label={
                  isExpanded
                    ? `${t('common.showLess')} - ${answer.answerValue}`
                    : `${t('common.showMore')} - ${answer.answerValue}`
                }
                aria-expanded={isExpanded}
              >
                {isExpanded ? t('common.showLess') : t('common.showMore')}
              </button>
            )}
            {/* Only show vote count if voting is enabled */}
            {isVotingEnabled && (
              <div className='flex items-center gap-1 mt-1'>
                <i
                  className='pi pi-thumbs-up-fill text-blue-700 text-xs'
                  aria-hidden='true'
                />
                <span className='text-sm font-semibold text-gray-900'>
                  {voteCount}{' '}
                  {voteCount === 1
                    ? t('voting.vote', { defaultValue: 'vote' })
                    : t('voting.votes', { defaultValue: 'votes' })}
                </span>
              </div>
            )}
          </div>

          {/* Vote Button - Only show if voting is enabled */}
          {isVotingEnabled && (
            <div className='mixer-card-vote-wrapper'>
              <VoteButton hasVoted={hasVoted} onClick={onVote} />
            </div>
          )}
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function - only re-render if these props change
    return (
      prevProps.answer.id === nextProps.answer.id &&
      prevProps.hasVoted === nextProps.hasVoted &&
      prevProps.voteCount === nextProps.voteCount &&
      prevProps.isVotingEnabled === nextProps.isVotingEnabled
    );
  }
);

MixerCard.displayName = 'MixerCard';

export default MixerCard;
